import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FilmStrip, CaretRight, Play, TrendUp, Users, Shield, Heart, CurrencyInr, Buildings, Sparkle, List, X } from '@phosphor-icons/react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { formatInrFriendly } from '../../lib/inr';
import LegalFooter from '../../components/LegalFooter';
import NotificationBell from '../../components/NotificationBell';
import { isNativeApp } from '../../lib/native';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../../components/ui/accordion';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const HERO_IMAGES = [
  'https://images.unsplash.com/photo-1485846234645-a62644f84728?w=1920&q=85',
  'https://images.unsplash.com/photo-1440404653325-ab127d49abc1?w=1920&q=85',
  'https://images.unsplash.com/photo-1542204165-65bf26472b9b?w=1920&q=85',
];

const REASONS = [
  { title: 'Earn royalties on hits', desc: 'Back the next blockbuster. Receive automatic royalty distributions as movies generate revenue.', icon: TrendUp },
  { title: 'Industry-native communities', desc: 'Separate hubs for Tollywood, Bollywood, Kollywood, Hollywood. Talk to people who get it.', icon: Users },
  { title: 'Producer-grade transparency', desc: 'Live funding bars, tier breakdowns, escrow status, revenue updates. No black-box accounting.', icon: Shield },
  { title: 'Discover before you back', desc: 'Watch funding momentum, read fan theories, and pick films that match your taste.', icon: Heart },
];

const FAQS = [
  { q: 'What is Fanzingy?', a: 'A marketplace where Investors, Producers, and Fans come together around films. Producers raise capital, investors earn royalty rights on box-office revenue, and fans discover, discuss, and back the movies they love.' },
  { q: 'How do royalties work?', a: 'Every movie has investment tiers (Silver / Gold / Executive Producer). Each tier has a royalty percentage. When the movie generates revenue, producers distribute royalties, and your share lands automatically.' },
  { q: 'Which film industries are supported?', a: 'Bollywood, Hollywood, Tollywood, and Kollywood — with industry-specific fan communities so you can join the right conversation, theory threads, and casting debates.' },
  { q: 'How much can I invest?', a: 'Producers set their own tiers — some open at as little as ₹50,000, others reserve seats for executive producers at ₹50,00,000+. Browse the marketplace for live options.' },
  { q: 'Is my money safe?', a: 'Yes. All investments are held in escrow until the movie reaches its funding goal. If the goal is not met by the deadline, your money is refunded automatically.' },
  { q: 'How does the fan community work?', a: 'Anyone with a Fanzingy account can post in the community — by industry or by movie. Like posts, leave comments, follow theories, and discover what other cinephiles are saying.' },
];

const ThemeHotstar = ({ user }) => {
  const navigate = useNavigate();
  const [movies, setMovies] = useState([]);
  const [stats, setStats] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const native = isNativeApp();

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/movies`).then((r) => setMovies(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    axios.get(`${BACKEND_URL}/api/public/stats`).then((r) => setStats(r.data && typeof r.data === 'object' ? r.data : null)).catch(() => {});
  }, []);

  // Scroll-driven horizontal parallax for the poster mosaic — gentle cinema-curtain drift.
  const { scrollY } = useScroll();
  const mosaicX = useTransform(scrollY, [0, 3000], [0, -180]);
  const mosaicY = useTransform(scrollY, [0, 3000], [0, 60]);

  const dashRoute = () => {
    if (user?.role === 'admin') return '/admin-dashboard';
    if (user?.role === 'producer') return '/producer-dashboard';
    if (user?.role === 'fan') return '/fan-dashboard';
    return '/investor-dashboard';
  };

  return (
    <div className="min-h-screen text-white relative" style={{ fontFamily: "'Poppins', system-ui, sans-serif" }}>
      {/* Animated movie-poster mosaic background (live across the whole page) */}
      <div className="fixed inset-0 z-0 overflow-hidden bg-black" data-testid="hero-poster-mosaic">
        {(() => {
          // Build a tile pool. Use real platform posters when available; otherwise unsplash fallback.
          const isPlausibleImage = (u) => {
            if (!u || typeof u !== 'string') return false;
            const lower = u.toLowerCase();
            if (lower.includes('placehold.co')) return false;
            return /\.(jpg|jpeg|png|webp|gif|avif)(\?|$)/i.test(lower) || lower.includes('/api/uploads/') || lower.includes('images.unsplash.com');
          };
          const realPosters = movies.map(m => m.poster_url).filter(isPlausibleImage);
          const pool = realPosters.length >= 6 ? realPosters : [...realPosters, ...HERO_IMAGES];
          const tiles = [];
          // In the app: fewer, static tiles and no scroll parallax — the full
          // animated wall made scrolling stutter badly on real phones.
          const cols = native ? 3 : 6, rows = 4;
          for (let i = 0; i < cols * rows; i++) {
            tiles.push(pool[i % pool.length]);
          }
          return (
            <motion.div
              className="absolute grid"
              style={{
                inset: '-8% -8%',  // overscan so parallax drift never reveals empty edges
                gridTemplateColumns: `repeat(${cols}, 1fr)`,
                gridTemplateRows: `repeat(${rows}, 1fr)`,
                gap: '4px',
                ...(native ? {} : { x: mosaicX, y: mosaicY }),
              }}
            >
              {tiles.map((src, i) => {
                // Stagger each tile's animation so the wall feels alive (no two tiles in lockstep)
                const dur = 8 + ((i * 7) % 13);   // 8s – 20s
                const delay = -((i * 1.3) % 11);  // negative delays kick in mid-cycle
                return (
                  <div
                    key={i}
                    className="fz-mosaic-tile"
                    style={{
                      backgroundImage: `url(${src})`,
                      animationDuration: `${dur}s`,
                      animationDelay: `${delay}s`,
                    }}
                  />
                );
              })}
            </motion.div>
          );
        })()}

        {/* Animated colour wash — pulses very slowly for a "live" stage feel */}
        <div className="absolute inset-0 pointer-events-none fz-colour-wash" />

        {/* Darkening + readability scrim */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              'linear-gradient(180deg, rgba(8,4,20,0.55) 0%, rgba(8,4,20,0.70) 40%, rgba(0,0,0,0.78) 100%)',
          }}
        />

        {/* Subtle film-grain noise */}
        <div
          className="absolute inset-0 pointer-events-none opacity-[0.07] mix-blend-overlay native-hide"
          style={{
            backgroundImage:
              "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/><feColorMatrix type='saturate' values='0'/></filter><rect width='100%' height='100%' filter='url(%23n)' opacity='0.7'/></svg>\")",
          }}
        />
      </div>

      <nav data-web-chrome="nav" className="relative z-10 px-6 py-4 max-w-7xl mx-auto flex justify-between items-center">
        <div className="flex items-center gap-3">
          <img src="/fanzingy-logo.jpeg" alt="FanZingy" className="h-10 w-10 rounded-full object-cover" data-testid="brand-logo" />
          <span
            className="text-2xl tracking-tight"
            style={{ fontFamily: "'Poppins', sans-serif", fontWeight: 900, letterSpacing: '-0.04em' }}
          >
            Fan<span style={{ background: 'linear-gradient(90deg, #f472b6, #c084fc, #22d3ee)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Zingy</span>
          </span>
        </div>
        <div className="flex items-center gap-2">
          <div className="hidden md:flex items-center gap-2">
            <button
              data-testid="theme-hotstar-marketplace"
              onClick={() => navigate('/marketplace')}
              className="px-4 py-2 text-sm font-semibold text-white/80 hover:text-white transition-colors"
            >
              Browse
            </button>
            <button
              onClick={() => navigate('/community')}
              className="px-4 py-2 text-sm font-semibold text-white/80 hover:text-white transition-colors"
            >
              Community
            </button>
            <button
              onClick={() => navigate('/fraternity')}
              data-testid="nav-fraternity"
              className="px-4 py-2 text-sm font-semibold text-white/80 hover:text-white transition-colors"
            >
              Fraternity
            </button>
          </div>
          <NotificationBell user={user} />
          {user ? (
            <button
              onClick={() => navigate(dashRoute())}
              className="px-4 sm:px-6 py-2.5 bg-white text-black rounded-md text-sm font-bold hover:bg-white/90 transition-all whitespace-nowrap"
            >
              Dashboard
            </button>
          ) : (
            <button
              onClick={() => navigate('/login')}
              className="px-4 sm:px-6 py-2.5 bg-white text-black rounded-md text-sm font-bold hover:bg-white/90 transition-all whitespace-nowrap"
            >
              Sign In
            </button>
          )}
          <button
            onClick={() => setMobileMenuOpen((v) => !v)}
            className="md:hidden text-white/80 hover:text-white p-1"
            aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
            data-web-chrome="menu"
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X size={24} /> : <List size={24} />}
          </button>
        </div>
      </nav>

      {mobileMenuOpen && (
        <div className="md:hidden relative z-20 max-w-7xl mx-auto px-6 pb-4 flex flex-col gap-1" data-web-chrome="menu" data-testid="mobile-menu-panel">
          <button
            onClick={() => { setMobileMenuOpen(false); navigate('/marketplace'); }}
            className="text-left px-4 py-3 rounded-md text-sm font-semibold text-white/80 hover:text-white hover:bg-white/5 transition-colors"
          >
            Browse
          </button>
          <button
            onClick={() => { setMobileMenuOpen(false); navigate('/community'); }}
            className="text-left px-4 py-3 rounded-md text-sm font-semibold text-white/80 hover:text-white hover:bg-white/5 transition-colors"
          >
            Community
          </button>
          <button
            onClick={() => { setMobileMenuOpen(false); navigate('/fraternity'); }}
            className="text-left px-4 py-3 rounded-md text-sm font-semibold text-white/80 hover:text-white hover:bg-white/5 transition-colors"
          >
            Fraternity
          </button>
        </div>
      )}

      <section className="relative z-10 max-w-7xl mx-auto px-6 pt-24 pb-32 native-home-hero">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="max-w-3xl"
        >
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-white/10 backdrop-blur-md border border-white/20 rounded-full mb-6 fz-pill-pulse" data-testid="hero-pill">
            <Sparkle size={14} weight="fill" className="text-[#facc15]" />
            <span className="text-xs font-semibold tracking-wide">Fans · Producers · Investors — together on one stage</span>
          </div>
          <h1
            className="text-4xl md:text-5xl lg:text-6xl mb-6 leading-[1.05] tracking-tight native-hero-title"
            style={{ fontFamily: "'Poppins', sans-serif", fontWeight: 900, letterSpacing: '-0.035em' }}
            data-testid="hero-headline"
          >
            Don't be just a <span className="fz-fire-text" data-testid="hero-fan">FAN</span>. Own a piece of the <span className="fz-fire-text" data-testid="hero-movie">MOVIE</span>.
          </h1>
          <p className="text-xl text-white/80 mb-3 max-w-2xl leading-relaxed font-light">
            Back films, short films, web series &amp; OTT projects across Tollywood, Bollywood, Kollywood &amp; Hollywood — and get paid like a producer.
          </p>
          <p className="text-base text-white/60 mb-10 max-w-2xl leading-relaxed font-light native-hide">
            Pick a project. Lock your tier. Earn royalties when it hits the box office or OTT.
          </p>
          <div className="flex flex-wrap gap-4 mb-6 native-hero-ctas">
            <button
              onClick={() => navigate('/register?role=fan')}
              data-testid="cta-join-fan"
              className="group px-7 py-4 bg-white text-black rounded-md text-base font-bold hover:bg-white/90 transition-all flex items-center gap-2"
            >
              <Heart size={18} weight="fill" className="text-[#f472b6]" /> Join as a Fan
              <CaretRight size={16} weight="bold" className="group-hover:translate-x-1 transition-transform" />
            </button>
            <button
              onClick={() => navigate('/register?role=investor')}
              data-testid="cta-join-investor"
              className="group px-7 py-4 bg-gradient-to-r from-[#facc15] to-amber-500 text-black rounded-md text-base font-bold hover:scale-[1.02] transition-transform flex items-center gap-2"
            >
              <TrendUp size={18} weight="bold" /> Invest in a film
              <CaretRight size={16} weight="bold" className="group-hover:translate-x-1 transition-transform" />
            </button>
            <button
              onClick={() => navigate('/register?role=producer')}
              data-testid="cta-join-producer"
              className="group px-7 py-4 bg-white/10 backdrop-blur-md border border-white/25 text-white rounded-md text-base font-semibold hover:bg-white/15 transition-all flex items-center gap-2"
            >
              <FilmStrip size={18} weight="bold" /> Pitch my project
            </button>
          </div>
          <div className="flex items-center gap-3 text-xs text-white/70 font-light">
            <button
              onClick={() => navigate('/marketplace')}
              data-testid="cta-browse"
              className="hover:text-white flex items-center gap-1.5 underline-offset-2 hover:underline"
            >
              <Play size={12} weight="fill" /> Just browsing? See live raises →
            </button>
          </div>
        </motion.div>
      </section>

      {/* Metric Tiles — platform vitals */}
      <section className="relative z-10 max-w-7xl mx-auto px-6 pb-20" data-testid="metrics-section">
        <div className="flex items-end justify-between mb-6">
          <div>
            <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight" style={{ letterSpacing: '-0.03em' }}>
              The numbers{' '}
              <span style={{ background: 'linear-gradient(90deg, #f472b6, #c084fc, #22d3ee)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                so far
              </span>
            </h2>
            <p className="text-sm text-white/70 mt-1 font-light">Live platform vitals — pulled in real time.</p>
          </div>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4" data-testid="metric-tiles">
          {[
            {
              key: 'funds',
              label: 'Funds Raised',
              value: stats ? `₹${formatInrFriendly(stats.funds_raised_inr) || '0'}` : '—',
              sub: 'across active projects',
              icon: CurrencyInr,
              ring: 'from-emerald-400/30 to-emerald-700/10 border-emerald-400/30',
              tone: 'text-emerald-300',
              testid: 'metric-funds-raised',
            },
            {
              key: 'projects',
              label: 'Projects Funded',
              value: stats ? String(stats.funded_projects) : '—',
              sub: 'films, shorts & series',
              icon: FilmStrip,
              ring: 'from-[#facc15]/25 to-[#f59e0b]/10 border-[#facc15]/30',
              tone: 'text-[#facc15]',
              testid: 'metric-projects-funded',
            },
            {
              key: 'investors',
              label: 'Total Investors',
              value: stats ? String(stats.total_investors) : '—',
              sub: 'backing the marketplace',
              icon: Users,
              ring: 'from-[#c084fc]/30 to-purple-700/10 border-[#c084fc]/30',
              tone: 'text-[#c084fc]',
              testid: 'metric-total-investors',
            },
            {
              key: 'houses',
              label: 'Production Houses',
              value: stats ? String(stats.production_houses_count) : '—',
              sub: 'producers onboard',
              icon: Buildings,
              ring: 'from-[#f472b6]/30 to-rose-700/10 border-[#f472b6]/30',
              tone: 'text-[#f472b6]',
              testid: 'metric-production-houses',
            },
          ].map((m, i) => {
            const Icon = m.icon;
            return (
              <motion.div
                key={m.key}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.06 }}
                className={`fz-tile-bob relative overflow-hidden rounded-xl p-5 sm:p-6 bg-gradient-to-br ${m.ring} border backdrop-blur-md`}
                style={{ animationDelay: `${i * 0.6}s` }}
                data-testid={m.testid}
              >
                <Icon size={28} weight="bold" className={`${m.tone} mb-3`} />
                <div className="text-2xl sm:text-4xl font-extrabold tabular-nums leading-none mb-2" style={{ letterSpacing: '-0.03em' }}>
                  {m.value}
                </div>
                <div className="text-[10px] sm:text-xs uppercase tracking-wider text-white/80 font-bold">{m.label}</div>
                <div className="text-[10px] sm:text-xs text-white/70 mt-1 font-light">{m.sub}</div>
              </motion.div>
            );
          })}
        </div>
        {stats?.industries_covered?.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 mt-6" data-testid="industries-pills">
            <span className="text-[10px] uppercase tracking-wider text-white/70 mr-1 font-semibold">Industries on platform:</span>
            {stats.industries_covered.map((ind) => (
              <span key={ind} className="text-[10px] uppercase tracking-wider px-3 py-1 rounded-full bg-white/10 border border-white/15 text-white/80 font-bold" data-testid={`industry-pill-${ind}`}>
                {ind}
              </span>
            ))}
          </div>
        )}
      </section>

      {/* Production Houses on Fanzingy */}
      {stats?.production_houses?.length > 0 && (
        <section className="relative z-10 max-w-7xl mx-auto px-6 pb-20" data-testid="production-houses-section">
          <div className="flex items-end justify-between mb-8">
            <div>
              <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight" style={{ letterSpacing: '-0.03em' }}>
                Production houses
                <span style={{ background: 'linear-gradient(90deg, #f472b6, #c084fc, #22d3ee)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                  on Fanzingy
                </span>
              </h2>
              <p className="text-sm text-white/70 mt-1 font-light">Studios, indie labels &amp; producers currently raising on the platform.</p>
            </div>
            <button
              data-testid="houses-see-all"
              onClick={() => navigate('/marketplace')}
              className="hidden sm:inline-flex text-sm text-white/60 hover:text-white font-semibold"
            >
              See all →
            </button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4" data-testid="production-houses-grid">
            {stats.production_houses.slice(0, 8).map((h, i) => {
              const initials = (h.label || '?').split(/\s+/).slice(0, 2).map((w) => w[0] || '').join('').toUpperCase();
              return (
                <motion.button
                  key={h.producer_id}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.05 }}
                  onClick={() => navigate(`/producer/${h.producer_id}`)}
                  data-testid={`house-card-${h.producer_id}`}
                  className="text-left bg-white/5 backdrop-blur-md border border-white/10 rounded-xl p-5 hover:border-[#c084fc]/50 hover:bg-white/[0.08] transition-all group"
                >
                  <div className="flex items-center gap-3 mb-4">
                    {h.avatar_url ? (
                      <img src={h.avatar_url} alt={h.label} className="w-12 h-12 rounded-lg object-cover ring-1 ring-white/20" />
                    ) : (
                      <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-[#f472b6] via-[#c084fc] to-[#22d3ee] flex items-center justify-center font-extrabold text-black text-sm shadow-lg">
                        {initials || '★'}
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-extrabold leading-tight truncate group-hover:text-[#c084fc] transition-colors">{h.label}</div>
                      {h.location && (
                        <div className="text-[10px] text-white/70 truncate mt-0.5 font-light">{h.location}</div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] uppercase tracking-wider text-white/60 font-semibold">
                      {h.movies_count} {h.movies_count === 1 ? 'project' : 'projects'}
                    </span>
                    <CaretRight size={14} className="text-white/70 group-hover:text-[#c084fc] transition-colors" />
                  </div>
                </motion.button>
              );
            })}
          </div>
        </section>
      )}

      {/* How it works — From fan to funder in 3 steps */}
      <section className="relative z-10 border-t border-white/10 py-20 bg-black/40 backdrop-blur-sm" data-testid="how-it-works-section">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-3xl md:text-5xl font-extrabold mb-3 tracking-tight text-center" style={{ letterSpacing: '-0.03em' }}>
            From fan to{' '}
            <span style={{ background: 'linear-gradient(90deg, #f472b6, #c084fc, #22d3ee)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
              funder
            </span>{' '}
            in 3 steps
          </h2>
          <p className="text-sm md:text-base text-white/70 mb-12 text-center font-light">No paperwork. No middlemen. Just movies and money.</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
            {[
              { n: '01', title: 'Pick a project', desc: 'Browse live films, shorts and web series raising on Fanzingy. Read the team, check the budget, filter by industry.', icon: FilmStrip },
              { n: '02', title: 'Lock your tier', desc: 'Choose FUNDER (fixed return), PAPPA (purchase-order markup) or KUBERA (OTT profit share). Pay securely via escrow.', icon: Shield },
              { n: '03', title: 'Earn from box office', desc: 'When the movie monetises — theatre, OTT or satellite — your royalty is transferred directly to your registered bank account.', icon: TrendUp },
            ].map((s, i) => {
              const Icon = s.icon;
              return (
                <motion.div
                  key={s.n}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="relative bg-white/5 border border-white/10 rounded-xl p-6 sm:p-7 overflow-hidden backdrop-blur-md hover:border-[#c084fc]/40 transition-colors"
                  data-testid={`how-step-${i + 1}`}
                >
                  <div className="absolute -top-3 -right-3 text-8xl font-extrabold text-white/[0.04] leading-none select-none" style={{ letterSpacing: '-0.05em' }}>
                    {s.n}
                  </div>
                  <Icon size={32} className="text-[#c084fc] mb-4 relative z-10" weight="bold" />
                  <h3 className="text-lg font-extrabold mb-2 relative z-10">{s.title}</h3>
                  <p className="text-sm text-white/60 leading-relaxed relative z-10 font-light">{s.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {movies.length > 0 && (
        <section className="relative z-10 max-w-7xl mx-auto px-6 pb-20">
          <div className="flex items-end justify-between mb-6">
            <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight" style={{ letterSpacing: '-0.03em' }}>
              Trending today
            </h2>
            <button
              onClick={() => navigate('/marketplace')}
              className="text-sm text-white/60 hover:text-white font-semibold"
            >
              See all →
            </button>
          </div>
          <div className="flex gap-4 overflow-x-auto pb-4 snap-x snap-mandatory scrollbar-hidden">
            {movies.slice(0, 10).map((m, i) => (
              <motion.button
                key={m.id}
                whileHover={{ y: -6 }}
                onClick={() => navigate(`/movie/${m.id}`)}
                className="relative flex-shrink-0 w-52 aspect-[2/3] rounded-xl overflow-hidden snap-start group shadow-2xl"
              >
                <img src={m.poster_url} alt={m.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/30 to-transparent" />
                <div className="absolute top-3 left-3 px-2 py-0.5 bg-gradient-to-r from-[#f472b6] to-[#c084fc] rounded-md text-[10px] font-bold">
                  #{i + 1}
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-3">
                  <div className="text-[10px] uppercase tracking-wider text-[#22d3ee] font-bold mb-1">{m.industry}</div>
                  <div className="text-base font-bold leading-tight">{m.title}</div>
                </div>
              </motion.button>
            ))}
          </div>
        </section>
      )}

      {/* Why Join */}
      <section className="relative z-10 border-t border-white/10 py-20 bg-black/40 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-3xl md:text-5xl font-extrabold mb-12 tracking-tight" style={{ letterSpacing: '-0.03em' }}>
            More reasons to{' '}
            <span style={{ background: 'linear-gradient(90deg, #f472b6, #c084fc, #22d3ee)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
              join
            </span>
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {REASONS.map((r, i) => {
              const Icon = r.icon;
              return (
                <motion.div
                  key={r.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.08 }}
                  className="relative rounded-xl p-6 bg-gradient-to-br from-purple-900/40 via-slate-900/60 to-black/60 border border-white/10 hover:border-[#c084fc]/40 transition-colors"
                  data-testid={`reason-card-${i}`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <h3 className="text-lg font-extrabold pr-4 leading-snug">{r.title}</h3>
                    <Icon size={32} className="text-[#c084fc] flex-shrink-0" weight="bold" />
                  </div>
                  <p className="text-sm text-white/60 leading-relaxed">{r.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="relative z-10 border-t border-white/10 py-20">
        <div className="max-w-3xl mx-auto px-6">
          <h2 className="text-3xl md:text-5xl font-extrabold mb-12 text-center tracking-tight" style={{ letterSpacing: '-0.03em' }}>
            Frequently asked
            <span style={{ background: 'linear-gradient(90deg, #f472b6, #c084fc, #22d3ee)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
              questions
            </span>
          </h2>
          <Accordion type="single" collapsible className="space-y-3" data-testid="faq-accordion">
            {FAQS.map((item, i) => (
              <AccordionItem
                key={item.q}
                value={`item-${i}`}
                className="bg-white/5 backdrop-blur-md border border-white/10 rounded-xl px-5 sm:px-7 data-[state=open]:border-[#c084fc]/40 transition-colors"
                data-testid={`faq-item-${i}`}
              >
                <AccordionTrigger className="text-base sm:text-lg font-bold py-5 hover:no-underline text-left">
                  {item.q}
                </AccordionTrigger>
                <AccordionContent className="text-sm sm:text-base text-white/70 leading-relaxed pb-5">
                  {item.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
          <div className="text-center mt-12">
            <p className="text-base text-white/60 mb-5">Ready to back your next favourite film?</p>
            <button
              data-testid="faq-cta-btn"
              onClick={() => navigate('/register')}
              className="px-10 py-4 bg-white text-black rounded-md text-base font-bold hover:bg-white/90 transition-all inline-flex items-center gap-2"
            >
              Get Started — Free
              <CaretRight size={16} weight="bold" />
            </button>
          </div>
        </div>
      </section>


      <LegalFooter />
    </div>
  );
};

export default ThemeHotstar;
